import { useDrag } from '@use-gesture/react';
import { motion } from 'motion/react';
import { type JSX, useState } from 'react';
import { useMeasure } from 'src/lib/player/utils/hooks';

const CLOSED_HEIGHT = 26;
const MIN_OPEN_HEIGHT = 230;
const MAX_HEIGHT_MARGIN = 30;

const GENTLE = { type: 'spring', stiffness: 120, damping: 14 } as const;
const STIFF = { type: 'spring', stiffness: 210, damping: 20 } as const;
const IMMEDIATE = { duration: 0 } as const;

export default function Sheet({
  maxHeight,
  children,
  isInitiallyOpen = false,
}: {
  maxHeight: number;
  children: (width: number, height: number) => JSX.Element;
  isInitiallyOpen: boolean;
}): JSX.Element {
  const [contentRef, contentSize] = useMeasure({ scroll: false });
  const [height, setHeight] = useState(isInitiallyOpen ? MIN_OPEN_HEIGHT : CLOSED_HEIGHT);
  const [transition, setTransition] = useState<typeof GENTLE | typeof STIFF | typeof IMMEDIATE>(
    STIFF,
  );
  const isOpen = height > CLOSED_HEIGHT;

  const snapTo = (newY: number, canceled: boolean) => {
    const correctedY = newY > maxHeight - MAX_HEIGHT_MARGIN ? maxHeight : newY;
    setTransition(canceled ? GENTLE : STIFF);
    setHeight(correctedY);
  };
  const moveTo = (newY: number) => {
    setTransition(IMMEDIATE);
    setHeight(Math.min(maxHeight, Math.max(newY, CLOSED_HEIGHT)));
  };
  const bind = useDrag(
    ({ velocity: [, vy], movement: [, my], memo = height, last, canceled, event, tap }) => {
      event.preventDefault();
      if (tap && !isOpen) {
        snapTo(MIN_OPEN_HEIGHT, canceled);
        return;
      }

      const newY = Math.min(memo - my, maxHeight);
      // when the user releases the sheet, we check whether it passed
      // the treshold for it to close, or if we reset it to its open position
      if (last) {
        if (newY < MIN_OPEN_HEIGHT * 0.5 || vy < -0.5) {
          snapTo(CLOSED_HEIGHT, canceled);
          return;
        }
        if (vy > 2) {
          snapTo(maxHeight, canceled);
          return;
        }
        if (newY <= MIN_OPEN_HEIGHT) {
          snapTo(MIN_OPEN_HEIGHT, canceled);
          return;
        }
        if (newY > maxHeight - MAX_HEIGHT_MARGIN) {
          snapTo(maxHeight, canceled);
          return;
        }
        moveTo(Math.round(newY));
      } else {
        // when the user keeps dragging, we just move the sheet according to
        // the cursor position
        moveTo(newY);
      }
      return memo;
    },
    {},
  );

  return (
    <motion.div
      className="info-sheet"
      ref={contentRef}
      initial={false}
      animate={{ height }}
      transition={transition}
    >
      <div
        {...bind()}
        style={{
          display: 'flex',
          justifyContent: 'center',
          position: 'relative',
          touchAction: 'none',
        }}
      >
        <div className="info-sheet-throbber" />
      </div>
      {isOpen && (
        <div style={{ position: 'relative' }}>
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: height - CLOSED_HEIGHT,
              overflow: 'hidden',
            }}
            onTouchMove={(ev) => {
              ev.stopPropagation();
            }}
          >
            {contentSize.width > 0 && children(contentSize.width, height - CLOSED_HEIGHT)}
          </div>
        </div>
      )}
    </motion.div>
  );
}
